import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
    Alert,
    Badge,
    Button,
    Card,
    Input,
    InputNumber,
    Modal,
    Space,
    Switch,
    Table,
    Typography,
    message,
} from 'antd';
import { DeleteOutlined, ReloadOutlined, SaveOutlined, SearchOutlined } from '@ant-design/icons';
import { API_URL } from '../../config';
import ozonOrdersStore from '../../stores/OzonOrdersStore';

const { Title } = Typography;

const RETURNS_FILTERS_KEY = 'stockReturnsFilters';

const QUICK_FILTERS = [
    { name: 'Баффы', value: 'BN-' },
    { name: 'Хобы', value: 'HB-PRO' },
    { name: 'SUM', value: 'BN-SUM' },
    { name: 'PRO', value: 'BN-PRO' },
    { name: 'ZIM', value: 'BN-ZIM' },
    { name: 'NEO', value: 'BN-NEO' },
    { name: 'JS', value: 'BN-JS' },
    { name: 'BS', value: 'BN-BS' },
];

const loadFilters = () => {
    try {
        const parsed = JSON.parse(localStorage.getItem(RETURNS_FILTERS_KEY) || '{}');
        return {
            search: typeof parsed.search === 'string' ? parsed.search : '',
            onlyPresent: Boolean(parsed.onlyPresent),
        };
    } catch {
        return { search: '', onlyPresent: false };
    }
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const StockReturns = () => {
    const [filters, setFilters] = useState(loadFilters);
    const [searchDraft, setSearchDraft] = useState(() => loadFilters().search);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [modified, setModified] = useState({});
    const [originalValues, setOriginalValues] = useState({});
    const [previewImage, setPreviewImage] = useState('');

    useEffect(() => {
        localStorage.setItem(RETURNS_FILTERS_KEY, JSON.stringify(filters));
    }, [filters]);

    const fetchData = useCallback(
        async (nextPage = 1, replace = true) => {
            setLoading(true);
            setError(null);
            try {
                const params = { page: nextPage, only_present: filters.onlyPresent };
                if (filters.search) params.search = filters.search;
                const response = await axios.get(`${API_URL}/v1/returns/items`, {
                    params,
                    headers: authHeaders(),
                });
                const items = response.data.items || [];
                const originals = Object.fromEntries(
                    items.map((item) => [item.article, item.quantity])
                );
                setData((prev) => (replace ? items : [...prev, ...items]));
                setOriginalValues((prev) => (replace ? originals : { ...prev, ...originals }));
                setPage(nextPage);
                setHasMore(nextPage < response.data.total_pages);
            } catch (err) {
                console.error('Error fetching returns:', err);
                setError('Не удалось загрузить возвраты. Попробуйте позже.');
            } finally {
                setLoading(false);
            }
        },
        [filters]
    );

    useEffect(() => {
        fetchData(1, true);
    }, [fetchData]);

    const applySearch = (value) => {
        const search = String(value ?? searchDraft).trim();
        setSearchDraft(search);
        setFilters((prev) => ({ ...prev, search }));
    };

    const handleQuantityChange = (article, value) => {
        const quantity = Number(value);
        if (!Number.isInteger(quantity) || quantity < 0) return;
        setModified((prev) => {
            const next = { ...prev };
            if (quantity === originalValues[article]) {
                delete next[article];
            } else {
                next[article] = quantity;
            }
            return next;
        });
    };

    const removeChange = (article) => {
        setModified((prev) => {
            const next = { ...prev };
            delete next[article];
            return next;
        });
    };

    const handleSave = async () => {
        const items = Object.entries(modified).map(([article, quantity]) => ({ article, quantity }));
        if (!items.length) return;
        setSaving(true);
        try {
            await axios.put(`${API_URL}/v1/returns/items`, { items }, { headers: authHeaders() });
            message.success(`Сохранено позиций: ${items.length}`);
            setModified({});
            await fetchData(1, true);
            void ozonOrdersStore.fetchReturns();
        } catch (err) {
            console.error('Error saving returns:', err);
            message.error(err.response?.data?.detail || 'Не удалось сохранить возвраты');
        } finally {
            setSaving(false);
        }
    };

    const columns = [
        {
            title: 'Изображение',
            dataIndex: 'primary_image',
            key: 'primary_image',
            width: 80,
            render: (src) =>
                src ? (
                    <img
                        src={src}
                        alt="product"
                        style={{
                            width: 40,
                            height: 40,
                            objectFit: 'contain',
                            borderRadius: 4,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            cursor: 'pointer',
                        }}
                        onClick={() => setPreviewImage(src)}
                    />
                ) : (
                    'Нет изображения'
                ),
        },
        { title: 'Артикул', dataIndex: 'article', key: 'article', width: 140 },
        { title: 'Наименование', dataIndex: 'shortName', key: 'shortName', ellipsis: true },
        { title: 'Цвет', dataIndex: 'Color', key: 'Color', width: '25%', ellipsis: true },
        {
            title: 'Возвраты',
            dataIndex: 'quantity',
            key: 'quantity',
            width: 120,
            render: (quantity, record) => {
                const changed = modified[record.article] !== undefined;
                return (
                    <InputNumber
                        min={0}
                        precision={0}
                        size="small"
                        value={changed ? modified[record.article] : quantity}
                        onChange={(value) => handleQuantityChange(record.article, value)}
                        style={{
                            width: '100%',
                            backgroundColor: changed ? '#f6ffed' : undefined,
                            borderColor: changed ? '#b7eb8f' : undefined,
                        }}
                    />
                );
            },
        },
    ];

    const modifiedEntries = Object.entries(modified);

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
                        <Title level={3} style={{ margin: 0 }}>
                            Возвраты
                        </Title>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => fetchData(1, true)}
                            loading={loading}
                        >
                            Обновить
                        </Button>
                    </Space>
                    <Space wrap>
                        <Input.Search
                            placeholder="Артикул, наименование или цвет"
                            prefix={<SearchOutlined />}
                            allowClear
                            value={searchDraft}
                            onChange={(event) => {
                                setSearchDraft(event.target.value);
                                if (!event.target.value) applySearch('');
                            }}
                            onSearch={applySearch}
                            style={{ width: 320 }}
                        />
                        <Space>
                            <Switch
                                checked={filters.onlyPresent}
                                onChange={(checked) =>
                                    setFilters((prev) => ({ ...prev, onlyPresent: checked }))
                                }
                            />
                            <span>Только в наличии</span>
                        </Space>
                    </Space>
                    <Space wrap>
                        {QUICK_FILTERS.map((filter) => (
                            <Button
                                key={filter.value}
                                size="small"
                                type={filters.search === filter.value ? 'primary' : 'default'}
                                onClick={() =>
                                    applySearch(filters.search === filter.value ? '' : filter.value)
                                }
                            >
                                {filter.name}
                            </Button>
                        ))}
                    </Space>
                </Space>
            </Card>

            {modifiedEntries.length > 0 && (
                <Card
                    size="small"
                    title={
                        <Space>
                            <span>Изменённые позиции</span>
                            <Badge count={modifiedEntries.length} style={{ backgroundColor: '#1890ff' }} />
                        </Space>
                    }
                >
                    <Space direction="vertical" style={{ width: '100%' }}>
                        {modifiedEntries.map(([article, quantity]) => {
                            const item = data.find((row) => row.article === article);
                            const oldValue = originalValues[article] ?? 0;
                            const delta = quantity - oldValue;
                            return (
                                <div
                                    key={article}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: 8,
                                        background: '#fafafa',
                                        borderRadius: 4,
                                    }}
                                >
                                    <Space size="middle" wrap>
                                        <strong>{article}</strong>
                                        <span style={{ color: '#1890ff' }}>
                                            {item?.shortName || 'Без названия'}
                                        </span>
                                        <span>
                                            {oldValue} → {quantity}
                                        </span>
                                        <span style={{ color: delta > 0 ? '#52c41a' : '#ff4d4f' }}>
                                            ({delta > 0 ? '+' : ''}
                                            {delta})
                                        </span>
                                    </Space>
                                    <Button
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => removeChange(article)}
                                        disabled={saving}
                                    />
                                </div>
                            );
                        })}
                        <Space>
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                onClick={handleSave}
                                loading={saving}
                            >
                                Сохранить ({modifiedEntries.length})
                            </Button>
                            <Button danger onClick={() => setModified({})} disabled={saving}>
                                Очистить
                            </Button>
                        </Space>
                    </Space>
                </Card>
            )}

            <Card bodyStyle={{ padding: 12 }}>
                {error && (
                    <Alert
                        type="error"
                        message="Ошибка"
                        description={error}
                        showIcon
                        closable
                        onClose={() => setError(null)}
                        style={{ marginBottom: 12 }}
                    />
                )}
                <Table
                    columns={columns}
                    dataSource={data}
                    rowKey="article"
                    loading={loading}
                    pagination={false}
                    size="small"
                    scroll={{ x: 720 }}
                    rowClassName={(record) =>
                        modified[record.article] !== undefined ? 'modified-row' : ''
                    }
                />
                {hasMore && (
                    <div style={{ textAlign: 'center', marginTop: 12 }}>
                        <Button
                            type="primary"
                            size="small"
                            loading={loading}
                            onClick={() => fetchData(page + 1, false)}
                        >
                            Подгрузить ещё
                        </Button>
                    </div>
                )}
            </Card>

            <Modal open={Boolean(previewImage)} footer={null} onCancel={() => setPreviewImage('')} width={800} centered>
                <img alt="preview" style={{ width: '100%' }} src={previewImage} />
            </Modal>
        </Space>
    );
};

export default StockReturns;
